"use client";

import React from "react";
import { Form, Input, Checkbox, Button } from "antd";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import useLoginStore from "./loginStore";
import useDataMainStore from "../../stores/dataMainStore";
import useThemeStore from "../../stores/themeStore.ts";
import DomainBadge from "../../components/DomainBadge.tsx";

export default function Login() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const {
    email,
    password,
    rememberMe,
    isLoading,
    error,
    formErrors,
    setEmail,
    setPassword,
    setRememberMe,
    setFormErrors,
    login,
  } = useLoginStore();

  const dataMainStore = useDataMainStore();
  const { theme, toggleTheme } = useThemeStore();

  const handleInputChange = (field: string, value: any) => {
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: null });
    }
    switch (field) {
      case "email":
        setEmail(value);
        break;
      case "password":
        setPassword(value);
        break;
      case "rememberMe":
        setRememberMe(value);
        break;
    }
  };

  const onFinish = async () => {
    await login(dataMainStore, navigate, toast);
  };

  return (
    <div className={`min-h-screen flex flex-col lg:flex-row transition-colors duration-300 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Left Column - Login Form */}
      <div className={`min-h-screen w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-12 relative ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>

        {/* Theme Toggle Button - Top Right */}
        <button
          onClick={toggleTheme}
          className={`absolute top-6 right-6 w-12 h-12 rounded-full flex items-center justify-center transition-all transform hover:scale-110 ${theme === 'dark'
            ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? (
            <i className="fa-solid fa-sun text-xl"></i>
          ) : (
            <i className="fa-solid fa-moon text-xl"></i>
          )}
        </button>

        <div className="w-full max-w-md z-10">
          <Link
            to="/admin/dashboard"
            className={`inline-flex items-center gap-2 mb-8 sm:mb-12 transition-colors font-medium ${theme === 'dark'
              ? 'text-gray-400 hover:text-white'
              : 'text-gray-500 hover:text-black'
              }`}
          >
            <i className="fa-solid fa-arrow-left text-sm"></i>
            <span className="text-sm">Back to Dashboard</span>
          </Link>

          <div className="mb-10">
            <div className="flex items-center justify-between mb-3">
              <h1 className={`text-4xl sm:text-5xl font-bold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-black'
                }`}>
                Hello again!
              </h1>
              {/* Domain Label */}
              <DomainBadge size="md" />
            </div>
            <p className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Welcome back to your admin dashboard.
            </p>
          </div>

          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={onFinish}
            className="space-y-6"
            onSubmitCapture={(e) => e.preventDefault()}
          >
            <Form.Item
              label={
                <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                  }`}>
                  Email Address
                </span>
              }
              name="email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
              validateStatus={formErrors.email ? "error" : ""}
              help={formErrors.email || ""}
            >
              <Input
                size="large"
                placeholder="name@example.com"
                className={`h-14 rounded-2xl border-transparent transition-all ${theme === 'dark'
                  ? 'bg-gray-800 text-white placeholder-gray-500 hover:bg-gray-700 focus:bg-gray-700'
                  : 'bg-gray-50 hover:bg-gray-100 focus:bg-white'
                  }`}
                value={email}
                onChange={(e) => handleInputChange("email", e.target.value)}
              />
            </Form.Item>

            <Form.Item
              label={
                <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                  }`}>
                  Password
                </span>
              }
              name="password"
              rules={[{ required: true, message: "Please enter your password" }]}
              validateStatus={formErrors.password ? "error" : ""}
              help={formErrors.password || ""}
            >
              <Input
                size="large"
                placeholder="Enter your password"
                className={`h-14 rounded-2xl border-transparent transition-all ${theme === 'dark'
                  ? 'bg-gray-800 text-white placeholder-gray-500 hover:bg-gray-700 focus:bg-gray-700'
                  : 'bg-gray-50 hover:bg-gray-100 focus:bg-white'
                  }`}
                bordered={false}
                // iconRender={(visible) =>
                //   visible ? <i className="fa-solid fa-eye text-[#FFCB37]"></i> : <i className={`fa-solid fa-eye-slash ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}></i>
                // }

                value={password}
                onChange={(e) => handleInputChange("password", e.target.value)}
              />
            </Form.Item>

            <div className="flex items-center justify-between mb-8">
              <Checkbox
                checked={rememberMe}
                onChange={(e) =>
                  handleInputChange("rememberMe", e.target.checked)
                }
                className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}
              >
                Keep me logged in
              </Checkbox>
            </div>

            {error && (
              <div className={`rounded-2xl p-4 text-sm mb-6 font-medium border ${theme === 'dark'
                ? 'bg-red-900/20 text-red-400 border-red-800'
                : 'bg-red-50 text-red-600 border-red-100'
                }`}>
                {error}
              </div>
            )}

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={isLoading}
                className="w-full h-14 bg-[#FFCB37] hover:bg-[#E6B000] text-black font-bold rounded-full text-lg shadow-lg shadow-yellow-200/50 border-none transition-all transform hover:-translate-y-1"
              >
                Sign In
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>

    </div>
  );
}
